import { useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";

import './breakout-rooms.scss';
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { PageModuleGroupModules, PageModuleType, SessionTabsLayoutTypes } from "../../../../../types/working-model";
import { replaceSpaceWithDash } from "../../../../../utils/utils";
import { ModuleMap } from "./editor-main";
import RoomCard from "../../../../live-event/session/session-modules/breakout-rooms/room-card";
import { updateWorkingEventSession } from "../../../../../store/actions/admin/create-event";
import useBreakoutRoom, { IReturnUseBreakoutRoomReturn } from "../../../../live-event/modules/breakout-rooms/use-breakout-rooms";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useIsBelowTheFold } from "../../../../../hooks/session.hooks";

interface IRenderModulesProps {
	groupUuid: string;
	isBreakoutRooms?: boolean;
	breakoutsOff?: boolean;
	template: string;
}

const RenderModules: React.FC<IRenderModulesProps> = ({ groupUuid, isBreakoutRooms, template, breakoutsOff }) => {
	const { closeBreakoutRoom, isBreakoutSessionHost }: IReturnUseBreakoutRoomReturn = useBreakoutRoom();
	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);
	const translationUpdates = useTypedSelector(state => state.LiveEventReducer.translationUpdates);
	const isSessionDetailsBelowTheFoldV2 = useIsBelowTheFold(workingSession);

	const dispatch = useDispatch();

	// for single session events, need to keep workingEvent updated with workingSession changes
	useEffect(() => {
		if (workingSession) {
			dispatch(updateWorkingEventSession(workingSession));
		}
	}, [dispatch, workingSession]);

	const modules = useMemo(() => {
		const moduleGroup = workingSession?.module_grouping?.find((moduleGroup: PageModuleGroupModules) => {
			return (moduleGroup.uuid === groupUuid);
		});
		return<>
			<div>
				{
					moduleGroup?.modules.map(moduleId => {
						const module = workingSession?.modules.find(module => module.id === moduleId);
						const tabLayout = replaceSpaceWithDash(workingSession?.layout?.tabs?.layout.toLowerCase() || SessionTabsLayoutTypes.Horizontal.toLowerCase());

						if (!module || !module.is_on || !moduleGroup.is_on) return null;

						// FUTURE DEV - hiding quizzes
						if (module.type === PageModuleType.quizzes) return null;

						const Module = ModuleMap[module.type];

						//isSession is for Blank module
						return <Module key={module.id} page_module={module} template={template} tabLayout={tabLayout} isSession />;
					})
				}
				
			</div>
			
		</>;

		// ignoring exhaustive for translation updates which must force a re-render
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workingSession, groupUuid, template, translationUpdates]);

	// Breakout Rooms are different than all of the other modules in that they don't have sub modules. But they are similar
	// in that they are located in the same area as the other modules so we added this separately here.
	const breakoutRooms = useMemo(() => {
		if (breakoutsOff) {
			return null;
		}

		return (
			<GoogleOAuthProvider clientId={process.env.REACT_APP_MEET_INTEGRATION_OAUTH_CLIENT_ID ?? ''}>
				<div className="editor-wrapper breakout-rooms">
					<div className="container">
						<div className="breakout-rooms-list">
							{workingSession?.breakout_rooms?.map(room => {
								if (!room) return null;
								return (
									<RoomCard
										key={room.id}
										room={room}
										template={template}
										session={workingSession}
										closeBreakoutRoom={closeBreakoutRoom}
										isBreakoutSessionHost={isBreakoutSessionHost}
									/>
								);
							})}
						</div>
					</div>
				</div>
			</GoogleOAuthProvider>
		);
		// ignoring exhaustive for translation updates which must force a re-render
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workingSession, template, breakoutsOff, translationUpdates]);


	return (
		<div className={isSessionDetailsBelowTheFoldV2 ? "session-modules-container-below-the-fold" : "session-modules-container"} >{isBreakoutRooms ? breakoutRooms : modules}</div>
	);
};

export default RenderModules;
